import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Edit, Trash2, Calendar } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useModulesForDropdown } from '@/hooks/useModulesForDropdown'

const OperatorTasks = () => {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [operatorInfo, setOperatorInfo] = useState<any>(null)
  const { toast } = useToast()
  const { modules: allModules, loading: modulesLoading } = useModulesForDropdown()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    module: '',
    domain: '',
    priority: false,
    due_date: ''
  })

  const domains = [
    { value: 'self', label: 'Self' },
    { value: 'wealth', label: 'Wealth' },
    { value: 'social', label: 'Social' }
  ]

  // Filter modules by selected domain using category field
  const availableModules = allModules.filter(module => {
    if (!formData.domain) return true // Show all if no domain selected
    
    // Use category field instead of searching title for keywords
    const category = module.category.toLowerCase()
    if (formData.domain === 'wealth') {
      return category === 'wealth'
    } else if (formData.domain === 'self') {
      return category === 'self'
    } else if (formData.domain === 'social') {
      return category === 'social'
    }
    return false
  })

  useEffect(() => {
    fetchOperatorInfo()
  }, [])

  useEffect(() => {
    if (operatorInfo) {
      fetchTasks()
    }
  }, [operatorInfo])

  const fetchOperatorInfo = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      const { data: operator } = await supabase
        .from('operators')
        .select('*')
        .eq('user_id', session.user.id)
        .single()
      
      setOperatorInfo(operator)
    }
  }

  const fetchTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('school_tasks')
        .select('*')
        .eq('school_id', operatorInfo.school_id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setTasks(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching tasks",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) return

    try {
      const taskData = {
        title: formData.title,
        description: formData.description || null,
        module: formData.module || null,
        domain: formData.domain || null,
        priority: formData.priority,
        due_date: formData.due_date || null,
        school_id: operatorInfo.school_id,
        operator_id: operatorInfo.id
      }

      if (editingTask) {
        const { error } = await supabase
          .from('school_tasks')
          .update(taskData)
          .eq('id', editingTask.id)
        
        if (error) throw error
        toast({ title: "Task updated successfully" })
      } else {
        const { error } = await supabase
          .from('school_tasks')
          .insert(taskData)
        
        if (error) throw error
        toast({ title: "Task created successfully" })
      }

      setDialogOpen(false)
      setEditingTask(null)
      setFormData({
        title: '',
        description: '',
        module: '',
        domain: '',
        priority: false,
        due_date: ''
      })
      fetchTasks()
    } catch (error: any) {
      toast({
        title: "Error saving task",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (task: any) => {
    setEditingTask(task)
    setFormData({
      title: task.title,
      description: task.description || '',
      module: task.module || '',
      domain: task.domain || '',
      priority: task.priority || false,
      due_date: task.due_date || ''
    })
    setDialogOpen(true)
  }

  const handleDelete = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return

    try {
      const { error } = await supabase
        .from('school_tasks')
        .delete()
        .eq('id', taskId)
      
      if (error) throw error
      toast({ title: "Task deleted successfully" })
      fetchTasks()
    } catch (error: any) {
      toast({
        title: "Error deleting task",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      module: '',
      domain: '',
      priority: false,
      due_date: ''
    })
    setEditingTask(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">School Tasks</h2>
          <p className="text-gray-600">Manage tasks for your school's students</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add New Task
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter task title"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter task description (optional)"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Select value={formData.domain} onValueChange={(value) => setFormData({ ...formData, domain: value, module: '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {domains.map(domain => (
                        <SelectItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="module">Module</Label>
                  <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder={modulesLoading ? "Loading..." : "Select module"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModules.map(module => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
                
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    id="priority"
                    type="checkbox"
                    checked={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="priority">Priority Task</Label>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setDialogOpen(false)
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Module</TableHead>
              <TableHead>Domain</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                  No tasks created yet. Click 'Add New Task' to create your first task.
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task: any) => (
                <TableRow key={task.id}>
                  <TableCell className="font-medium">{task.title}</TableCell>
                  <TableCell>{task.module || 'N/A'}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">
                      {task.domain || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {task.priority ? (
                      <Badge variant="destructive">High</Badge>
                    ) : (
                      <Badge variant="secondary">Normal</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.due_date ? (
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {new Date(task.due_date).toLocaleDateString()}
                      </div>
                    ) : (
                      'No due date'
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(task)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(task.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default OperatorTasks
